import React, { useState } from 'react';
import { Container, Card, Row as BootstrapRow, Col as BootstrapCol, Button } from 'react-bootstrap';
import { Table, Select, Modal, Form } from 'antd';
import { FilterOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import FormItem from '../common/FormItem/FormItem';
import PageTitle from '../common/PageTitle/PageTitle';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import styles from './pricing-music-plans.module.scss';

interface MusicPlan {
  id: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  discount?: number;
  promotionalCode?: string;
  status: 'active' | 'inactive';
  planType: 'exclusive' | 'commercial' | 'enterprise';
}

const PricingMusicPlans: React.FC = () => {
  const [showMusicPlanModal, setShowMusicPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<MusicPlan | null>(null);
  const [filters, setFilters] = useState({
    planName: undefined as string | undefined,
    planType: undefined as string | undefined,
    status: undefined as string | undefined
  });
  const [resetCounter, setResetCounter] = useState(0);

  // Mock data for music plans
  const [musicPlans, setMusicPlans] = useState<MusicPlan[]>([
    { 
      id: '1', 
      name: 'Exclusive Plan', 
      monthlyPrice: 99, 
      annualPrice: 990, 
      status: 'active',
      planType: 'exclusive'
    },
    { 
      id: '2', 
      name: 'Commercial Plan', 
      monthlyPrice: 199, 
      annualPrice: 1990, 
      status: 'active',
      planType: 'commercial'
    },
    { 
      id: '3', 
      name: 'Enterprise Plan', 
      monthlyPrice: 499, 
      annualPrice: 4990, 
      status: 'active',
      planType: 'enterprise'
    }
  ]);

  // Filter options
  const filterOptions = {
    planName: musicPlans.map(plan => ({ value: plan.name, label: plan.name })),
    planType: [
      { value: 'exclusive', label: 'Exclusive' },
      { value: 'commercial', label: 'Commercial' },
      { value: 'enterprise', label: 'Enterprise' }
    ],
    status: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' }
    ]
  };

  // Filter the plans based on current filters
  const filteredPlans = musicPlans.filter(plan => {
    if (filters.planName && !plan.name.toLowerCase().includes(filters.planName.toLowerCase())) return false;
    if (filters.planType && plan.planType !== filters.planType) return false;
    if (filters.status && plan.status !== filters.status) return false;
    return true;
  });

  const handleFilterChange = (filterName: string, value: string | undefined) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleReset = () => {
    setFilters({ planName: undefined, planType: undefined, status: undefined });
    setResetCounter(prev => prev + 1);
  };

  const handleSaveMusicPlan = () => {
    console.log('Saving music plan...');
    setShowMusicPlanModal(false);
    setEditingPlan(null);
  };

  const handleEditMusicPlan = (plan: MusicPlan) => {
    setEditingPlan(plan);
    setShowMusicPlanModal(true);
  };

  const handleCreatePlan = () => {
    setEditingPlan(null);
    setShowMusicPlanModal(true);
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Pricing Management Music Plans" />
            <Button 
              onClick={handleReset}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <Form layout="vertical" className={styles.form}>
            <BootstrapRow>
              <BootstrapCol>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Plan Name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`planName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any plan name"
                            value={filters.planName}
                            onChange={value => handleFilterChange('planName', value)}
                            options={filterOptions.planName}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Plan Type"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`planType-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any plan type"
                            value={filters.planType}
                            onChange={value => handleFilterChange('planType', value)}
                            options={filterOptions.planType}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`status-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any status"
                            value={filters.status}
                            onChange={value => handleFilterChange('status', value)}
                            options={filterOptions.status}
                          />
                        </FormItem>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol 
                        md={12}
                        className="d-flex align-items-center justify-content-end mt-2 mt-md-0"
                      >
                        <Button
                          style={{ 
                            background: 'white', 
                            color: '#e21e26', 
                            border: '1px solid #e21e26', 
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            justifyContent: 'center',
                            height: '50px',
                            marginTop: '8px',
                            width: '200px'
                          }}
                          onClick={handleCreatePlan}
                        >
                          <PlusOutlined style={{ fontSize: 16 }} />
                          Add New Music Plan
                        </Button>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
              </BootstrapCol>
            </BootstrapRow>
          </Form>
          
          {/* Music Plans Grid */}
          <BootstrapRow>
            {filteredPlans.map((plan) => (
              <BootstrapCol md={4} key={plan.id} className="mb-3">
                <Card className={styles.planCard}>
                  <Card.Header className={styles.planHeader}>
                    <h5>{plan.name}</h5>
                    <Button 
                      variant="link" 
                      size="sm"
                      onClick={() => handleEditMusicPlan(plan)}
                      className={styles.editButton}
                    >
                      <EditOutlined />
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <div className={styles.priceInfo}>
                      <div className={styles.priceRow}>
                        <span>Monthly:</span>
                        <span className={styles.price}>${plan.monthlyPrice}</span>
                      </div>
                      <div className={styles.priceRow}>
                        <span>Annual:</span>
                        <span className={styles.price}>${plan.annualPrice}</span>
                      </div>
                      {plan.discount && (
                        <div className={styles.priceRow}>
                          <span>Discount:</span>
                          <span className={styles.discount}>{plan.discount}%</span>
                        </div>
                      )}
                      {plan.promotionalCode && (
                        <div className={styles.priceRow}>
                          <span>Promo Code:</span>
                          <span className={styles.promoCode}>{plan.promotionalCode}</span>
                        </div>
                      )}
                      <div className={styles.priceRow}>
                        <span>Status:</span>
                        <span className={`${styles.statusBadge} ${styles[plan.status]}`}>
                          {plan.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </BootstrapCol>
            ))}
          </BootstrapRow>

          {filteredPlans.length === 0 && (
            <div className={styles.noResults}>
              <p>No music plans found matching your filters.</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Music Plan Modal */}
      <Modal
        open={showMusicPlanModal}
        onCancel={() => setShowMusicPlanModal(false)}
        onOk={handleSaveMusicPlan}
        okText={editingPlan ? 'Update Plan' : 'Add Plan'}
        title={editingPlan ? 'Edit Music Plan' : 'Add New Music Plan'}
        width={600}
      >
        <div>
          <p>Music Plan form would go here.</p>
          <p>This is a placeholder for the music plan creation/editing functionality.</p>
        </div>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(PricingMusicPlans);
